import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  CircularProgress
} from '@mui/material';
import { CheckCircle, Warning, Launch } from '@mui/icons-material';
import { OAuthService } from '../services/OAuthService';

interface OAuthStatus {
  configured: boolean;
  authorized: boolean;
  clientId?: string;
  error?: string;
}

const GmailSetup: React.FC = () => {
  const [status, setStatus] = useState<OAuthStatus>({ configured: false, authorized: false });
  const [loading, setLoading] = useState(true);
  const [authCode, setAuthCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    checkOAuthStatus();
  }, []);

  const checkOAuthStatus = async () => {
    try {
      const response = await OAuthService.getStatus();
      setStatus(response.data);
      
      if (response.data.authorized) {
        setActiveStep(2);
      } else if (response.data.configured) {
        setActiveStep(1);
      } else {
        setActiveStep(0);
      }
    } catch (error) {
      console.error('Failed to check OAuth status:', error);
      setMessage({ type: 'error', text: 'Failed to check Gmail setup status' });
    } finally {
      setLoading(false);
    }
  };

  const handleStartAuth = async () => {
    setProcessing(true);
    setMessage(null);

    try {
      const response = await OAuthService.getAuthUrl();
      window.open(response.data.authUrl, '_blank');
      setActiveStep(1);
      setMessage({ 
        type: 'success', 
        text: 'Authorization URL opened in new tab. Please complete the authorization and paste the code below.' 
      });
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to start authorization'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCompleteAuth = async () => {
    if (!authCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter the authorization code' });
      return;
    }

    setProcessing(true);
    setMessage(null);

    try {
      await OAuthService.completeAuth(authCode);
      setMessage({ type: 'success', text: 'Gmail authorization completed successfully!' });
      setActiveStep(2);
      checkOAuthStatus();
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to complete authorization'
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const steps = [
    'Upload Gmail Credentials',
    'Authorize Gmail Access',
    'Setup Complete'
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Gmail OAuth Setup
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="body2">
            Status:
          </Typography>
          <Chip
            icon={status.authorized ? <CheckCircle /> : <Warning />}
            label={status.authorized ? 'Authorized' : status.configured ? 'Credentials Uploaded' : 'Not Configured'}
            color={status.authorized ? 'success' : status.configured ? 'warning' : 'error'}
            size="small"
          />
          {status.clientId && (
            <Typography variant="caption" color="text.secondary">
              Client ID: {status.clientId}
            </Typography>
          )}
        </Box>

        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Upload Gmail API Credentials</StepLabel>
            <StepContent>
              <Typography variant="body2" paragraph>
                First, upload your Gmail API credentials JSON file from Google Cloud Console in the Configuration tab.
              </Typography>
              {status.configured && (
                <Alert severity="success">
                  ✓ Gmail credentials have been uploaded successfully
                </Alert>
              )}
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Authorize Gmail Access</StepLabel>
            <StepContent>
              <Typography variant="body2" paragraph>
                Click "Start Authorization" to open Google's authorization page in a new tab.
                After granting permission, copy the authorization code and paste it below.
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleStartAuth}
                  disabled={!status.configured || processing}
                  startIcon={processing ? <CircularProgress size={16} /> : <Launch />}
                  sx={{ mb: 2 }}
                >
                  {processing ? 'Opening...' : 'Start Authorization'}
                </Button>
              </Box>

              <TextField
                fullWidth
                label="Authorization Code"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                placeholder="Paste the authorization code here"
                margin="normal"
                multiline
                rows={2}
              />
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCompleteAuth}
                  disabled={!authCode.trim() || processing}
                  startIcon={processing ? <CircularProgress size={16} /> : undefined}
                >
                  {processing ? 'Processing...' : 'Complete Authorization'}
                </Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Setup Complete</StepLabel>
            <StepContent>
              <Alert severity="success">
                ✓ Gmail authorization is complete! The email assistant can now access your Gmail account
                to read unread emails and forward invoices.
              </Alert>
            </StepContent>
          </Step>
        </Stepper>

        {status.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {status.error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default GmailSetup;